/**
 * Manage, Onboarding templates: the list (plan docs/plans/lms-plan.md 12.1,
 * 12.5, 18.12, 18.24; WP-47; R14).
 *
 * GET /admin/onboarding/templates: the network's templates, the default
 * first, each with its active item count, and its state (active or retired).
 * The name opens the editor. "New template" opens NewTemplateModal.
 *
 * 12.5: the default template comes from the owner's checklist document (the
 * seed of 18.12, 38 items); until a template exists the list is empty and
 * says so.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ClipboardList, Loader2, Plus } from 'lucide-react';
import EmptyState from '../../../../ui/EmptyState';
import { primaryBtnCls, secondaryBtnCls } from '../../../../ui/styles';
import { listOnboardingTemplates } from '../../../api/onboarding';
import type { OnboardingTemplateListItem } from '../../../api/onboarding';
import {
  ErrorNote,
  metaCls,
  Pill,
  rowCls,
  tableCardCls,
  tableCls,
  tbodyCls,
  tdCls,
  thCls,
  theadCls,
} from '../../../components/pickers/shared';
import { describeOnboardingError } from '../../onboarding/shared';
import NewTemplateModal from './NewTemplateModal';
import { templateHref } from './paths';

const PAGE = 200;

export function TemplateList() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<OnboardingTemplateListItem[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const request = useRef(0);

  const load = useCallback((offset: number) => {
    const id = ++request.current;
    setLoading(true);
    setError(null);
    listOnboardingTemplates({ limit: PAGE, offset })
      .then((page) => {
        if (id !== request.current) return;
        const items = Array.isArray(page.items) ? page.items : [];
        setRows((prev) => (offset === 0 ? items : [...prev, ...items]));
        setHasMore(Boolean(page.page?.hasMore));
      })
      .catch((err) => {
        if (id !== request.current) return;
        setError(describeOnboardingError(err, 'The onboarding templates could not be loaded.'));
      })
      .finally(() => {
        if (id === request.current) setLoading(false);
      });
  }, []);

  useEffect(() => {
    load(0);
  }, [load]);

  return (
    <div className="space-y-4" data-testid="onboarding-templates">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className={metaCls}>
          {"A template is the checklist a new team member's onboarding copies: the things done outside the platform, who does each one, and when."}
        </p>
        <button type="button" className={primaryBtnCls} onClick={() => setCreating(true)} data-testid="onboarding-template-new">
          <Plus className="h-4 w-4" aria-hidden="true" />
          New template
        </button>
      </div>

      {error ? (
        <ErrorNote testId="onboarding-templates-error">
          {error}{' '}
          <button type="button" className="font-medium underline" onClick={() => load(0)}>
            Retry
          </button>
        </ErrorNote>
      ) : null}

      {!error && !loading && rows.length === 0 ? (
        <div data-testid="onboarding-templates-empty">
          <EmptyState
            icon={ClipboardList}
            variant="card"
            title="No onboarding template yet"
            hint="The default template is made from your onboarding checklist document when the Learning Center is installed. Until then, create one here: onboarding cannot start without a template."
            action={
              <button type="button" className={primaryBtnCls} onClick={() => setCreating(true)}>
                <Plus className="h-4 w-4" aria-hidden="true" />
                New template
              </button>
            }
          />
        </div>
      ) : null}

      {rows.length > 0 ? (
        <div className={tableCardCls}>
          <table className={tableCls}>
            <thead className={theadCls}>
              <tr>
                <th scope="col" className={thCls}>
                  Template
                </th>
                <th scope="col" className={`${thCls} text-right`}>
                  Items
                </th>
                <th scope="col" className={thCls}>
                  Status
                </th>
              </tr>
            </thead>
            <tbody className={tbodyCls}>
              {rows.map((t) => (
                <tr key={t.id} className={rowCls} data-testid={`onboarding-template-row-${t.id}`}>
                  <td className={tdCls}>
                    <div className="flex flex-wrap items-center gap-2">
                      <Link
                        to={templateHref(t.id)}
                        className="font-medium text-primary-700 hover:underline focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-primary-400"
                      >
                        {t.name}
                      </Link>
                      {t.is_default ? <Pill tone="primary">Default</Pill> : null}
                    </div>
                    {t.description ? <p className={`${metaCls} mt-0.5 line-clamp-1`}>{t.description}</p> : null}
                  </td>
                  <td className={`${tdCls} text-right tabular-nums`}>{(t.item_count ?? 0).toLocaleString()}</td>
                  <td className={tdCls}>{t.is_active !== false ? <Pill tone="green">Active</Pill> : <Pill tone="gray">Retired</Pill>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      <div className="flex items-center gap-2">
        {loading ? <Loader2 aria-label="Loading templates" className="h-5 w-5 animate-spin text-primary-600 dark:text-primary-400" /> : null}
        {hasMore && !loading ? (
          <button type="button" className={secondaryBtnCls} onClick={() => load(rows.length)}>
            Show more templates
          </button>
        ) : null}
      </div>

      <NewTemplateModal isOpen={creating} onClose={() => setCreating(false)} onOpenTemplate={(id) => navigate(templateHref(id))} />
    </div>
  );
}

export default TemplateList;
